import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "./Mileage.css";
import { updateMileage, createMileage, deleteMileage } from '../../graphql/mutations';
import { listMileages, listDeletedUserHistories } from '../../graphql/queries';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import useFetchUserByEmail from '../../components/UserInfoLoader/LoadUserInfo';
import { capitalize } from '../../components/common/capitalize';
import XLSX from 'xlsx-js-style';
import MapView from './MapView';
import html2canvas from 'html2canvas';
import { generatePDFWithJsPDF } from './PDFRouteExporter';
import { mapquestFetch } from './MapQuestFetch';

export default function MileageLogView() {
  const [costPerMile, setCostPerMile] = useState(0.7);
  const [showForm, setShowForm] = useState(false);
  const [log, setLog] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [userEmail, setUserEmail] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [addressValidated, setAddressValidated] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [odoCorrectedOnce, setOdoCorrectedOnce] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [submittedLogs, setSubmittedLogs] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: 'travel_date',
    direction: 'ascending',
  });
  const formatDate = (date) => date ? new Date(date).toISOString().split('T')[0] : null;
  const formattedCurrent_Date = formatDate(new Date());
  const [originalEntry, setOriginalEntry] = useState(null);
  const [totalMileage, setTotalMileage] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showSubmittedLogs, setShowSubmittedLogs] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [odometerResetConfirmed, setOdometerResetConfirmed] = useState(false);

  const [stopTimes, setStopTimes] = useState({});
  const [contactPerson, setContactPerson] = useState({});
  const [isExporting, setIsExporting] = useState(false);

  const [newEntry, setNewEntry] = useState({
    id: '',
    name_employee: '',
    current_date: formattedCurrent_Date,
    cost_mileage: '',
    total_mileage_recorded: '',
    total_travel_cost: '',
    travel_date: '',
    time_in: '',
    time_out: '',
    start_address: '2300 W Commerce, San Antonio, TX 78207',
    end_address: '2300 W Commerce, San Antonio, TX 78207',
    contact_person: '',
    stop_address: [""],// Temp
    odometer_start: '',
    odometer_finish: '',
    mileage: '',
    notes: '',
    status: 'draft',
    userId: '',
  });

  const customAddresses = {
    "alexandra.valencia@healthcollaborative.net": {
      from: "6757 Stone Oak, Brownsville, TX 78526",
      to: "6757 Stone Oak, Brownsville, TX 78526",
    },
    "stephanie.constante@healthcollaborative.net": {
      from: "702 S Clarkwood Rd. Corpus Christi, Tx 78406",
      to: "702 S Clarkwood Rd. Corpus Christi, Tx 78406",
    }
  };

  const truncateToTwoDecimals = (num) => {
    return Math.floor(num * 100) / 100;
  };

  const fetchUserDetails = async () => {
    try {
      const { signInDetails } = await getCurrentUser();
      const session = await fetchAuthSession();
      const groups = session.tokens.idToken.payload['cognito:groups'] || [];
      setUserGroups(groups);
      setUserEmail(signInDetails.loginId.toLowerCase());

    } catch (error) {
      console.log('User not authenticated', error);
    }
  };
  useEffect(() => {
    fetchUserDetails();
  }, []);


  useEffect(() => {
    setAddressValidated(false);
  }, [newEntry.start_address, newEntry.end_address]);


  const { userInfo: userLoggedIn } = useFetchUserByEmail(userEmail);
  const isAdmin = userGroups?.some(group => group === 'Admin');
  const isManager = userGroups?.some(group => group === 'Manager');
  const isDev = userGroups?.some(group => group === 'dev');

  const [fetchMileages, { data, fetchMore }] = useLazyQuery(listMileages);
  const [fetchPreviousEmployees, { data: dataUser }] = useLazyQuery(listDeletedUserHistories, {
    fetchPolicy: "network-only"
  });

  const [addMileage] = useMutation(createMileage, {
    refetchQueries: [{ query: listMileages, variables: { limit: 1000 } }]
  });

  const [removeMileage] = useMutation(deleteMileage, {
    refetchQueries: [{ query: listMileages, variables: { limit: 1000 } }]
  });

  const [updMileage] = useMutation(updateMileage, {
    refetchQueries: [{ query: listMileages, variables: { limit: 1000 }, fetchPolicy: "network-only" }]
  });

  // Previous Employees
  useEffect(() => {
    if (!dataUser?.listDeletedUserHistories?.items) {
      fetchPreviousEmployees();
    }
  }, [fetchPreviousEmployees, dataUser]);

  // // Checkbox to select multiple logs
  const handleCheckboxChange = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = sortedSubmittedLogs.map(entry => entry.id);
      setSelectedIds(allIds);
      setAllSelected(true);
    } else {
      setSelectedIds([]);
      setAllSelected(false);
    }
  };


  const handleBulkStatusChange = async (newStatus = "draft") => {
    const updatedLogs = [...log];
    const updatedSubmitted = [...submittedLogs];

    for (let id of selectedIds) {
      const logIndex = updatedLogs.findIndex(e => e.id === id);
      if (logIndex !== -1) {
        updatedLogs[logIndex] = {
          ...updatedLogs[logIndex],
          status: newStatus,
        };
      }

      const submittedIndex = updatedSubmitted.findIndex(e => e.id === id);
      if (submittedIndex !== -1) {
        updatedSubmitted[submittedIndex] = {
          ...updatedSubmitted[submittedIndex],
          status: newStatus,
        };
      }


      try {
        await updMileage({
          variables: {
            input: {
              id,
              status: newStatus,
            },
          },
        });

      } catch (error) {
        console.error(`Error updating status for ID ${id}:`, error);
      }

    }
    toast.success('Logs changed to draft status successfully.');

    setLog(updatedLogs);
    setSubmittedLogs(updatedSubmitted);
    setSelectedIds([]);
    setAllSelected(false);
  };

  // Export All Submitted Logs 
  const exportToExcelSubmittedLogs = () => {

    const exportData = sortedSubmittedLogs.map(log => ({
      'Travel Date': formatDateMMDDYYYY(log.travel_date),
      'Driving Start Time': formatToAmPm(log.time_in),
      'Driving End Time': formatToAmPm(log.time_out),
      'From': log.start_address,
      'To': log.end_address,
      'Contact Person': log.contact_person,
      'Odometer Start': (parseFloat(log.odometer_start || 0)),
      'Odometer Finish': (parseFloat(log.odometer_finish || 0)),
      'Mileage': (parseFloat(log.mileage || 0)),
      'Navigator': `${log?.user?.first_name} ${log?.user?.last_name}`,
      'Status': log.status,
      'Event Name or Location': log.notes
    }));



    const worksheet = XLSX.utils.json_to_sheet(exportData);

    worksheet["!cols"] = [
      { wch: 12 },  // Date
      { wch: 15 },  // Driving Start Time
      { wch: 15 },  // Driving End Time
      { wch: 45 },  // From
      { wch: 45 },  // To
      { wch: 30 },  // Contact Person
      { wch: 10 },  // Odometer Start
      { wch: 10 },  // Odometer End
      { wch: 10 },  // Mileage
      { wch: 20 },  // Nav
      { wch: 10 },  // Status
      { wch: 30 },  // Notes
    ];


    const headerStyle = {
      font: {
        bold: true,
        sz: 16,
        color: { rgb: "FFFFFF" }
      },
      fill: { fgColor: { rgb: "0070C0" } },
    };

    const headerKeys = Object.keys(exportData[0]);
    headerKeys.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });
      if (!worksheet[cellAddress]) return;
      worksheet[cellAddress].s = headerStyle;
    });


    // Apply 0.00 number format to odometer and mileage columns (G, H, I)
    const rows = sortedSubmittedLogs.length;
    for (let i = 0; i < rows; i++) {
      const rowNum = i + 1;
      ['H', 'I', 'J'].forEach(col => {
        const cellRef = `${col}${rowNum + 1}`;
        if (worksheet[cellRef]) {
          worksheet[cellRef].z = '0.00';
        }
      });
    }


    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Mileage Log");

    XLSX.writeFile(workbook, "All_Submitted_Mileage_Logs.xlsx", { cellStyles: true })
  };

  //useEffect to load all mileage with pagination
  useEffect(() => {
    const fetchAllMileages = async () => {
      let AllMileages = [];
      let currentNextToken = null;

      do {
        const { data } = await fetchMore({
          variables: { limit: 100, nextToken: currentNextToken },
        });

        AllMileages = [...AllMileages, ...data.listMileages.items];
        currentNextToken = data.listMileages.nextToken;
      } while (currentNextToken);

      setLog(AllMileages);
      setNextToken(currentNextToken);
    };

    if (data) {
      fetchAllMileages();
    }
  }, [data, fetchMore]);



  const loadDraftLogs = async () => {
    let allLogs = [];
    let nextToken = null;

    do {
      const { data } = await fetchMore({
        variables: { limit: 100, nextToken },
      });

      const items = data?.listMileages?.items || [];
      allLogs = [...allLogs, ...items];
      nextToken = data?.listMileages?.nextToken;
    } while (nextToken);

    const draftLogs = allLogs.filter(item =>
      item.status === 'draft' &&
      (isAdmin || isManager ? true : item.userId === userLoggedIn?.id)
    );
    setLog(draftLogs);
  };

  useEffect(() => {
    loadDraftLogs();
  }, []);


  //Admin
  const loadSavedLogs = async () => {
    setShowSubmittedLogs(true)
    try {
      const { data } = await fetchMore({
        variables: { limit: 1000 },
        fetchPolicy: "network-only"
      });

      let logs = data?.listMileages?.items || [];
      const filtered = (isAdmin || isManager)
        ? logs.filter(item => item.status === "submitted")
        : logs.filter(item =>
          item.status === "submitted" && item.userId === userLoggedIn?.id
        );
      setSubmittedLogs(filtered);
    } catch (error) {
      console.error("Error loading submitted logs:", error);
    }
  };

  useEffect(() => {
    const draftLogs = log.filter(
      entry => entry.status === "draft" && entry.userId === userLoggedIn?.id
    );

    if (draftLogs.length === 0) {
      setTotalMileage(0);
      setTotalCost(0);
      return; //Avoid calculating with empty data
    }

    const mileage = draftLogs.reduce((sum, entry) => {
      const start = parseFloat(entry.odometer_start);
      const end = parseFloat(entry.odometer_finish);
      return sum + (isNaN(end - start) ? 0 : (end - start));
    }, 0);

    setTotalMileage((mileage));
    setTotalCost((mileage * costPerMile));
  }, [log, costPerMile, userLoggedIn?.id]);

  useEffect(() => {
    const updatedRows = document.querySelectorAll('.updated-row');
    updatedRows.forEach((el) => {
      el.classList.add('highlight-active');
      setTimeout(() => el.classList.remove('highlight-active'), 3000);
    });
  }, [log]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedEntry = { ...newEntry, [name]: value };

    if (name === 'odometer_start' || name === 'mileage') {
      const odoStart = parseFloat(name === 'odometer_start' ? value : updatedEntry.odometer_start);
      const mileage = parseFloat(name === 'mileage' ? value : updatedEntry.mileage);

      if (!isNaN(odoStart) && !isNaN(mileage)) {
        updatedEntry.odometer_finish = (odoStart + mileage);
      }
    }

    setNewEntry(updatedEntry);
  };


  const clearnewEntry = () => {
    const userEmailLower = userEmail?.toLowerCase();
    const custom = customAddresses[userEmailLower] || {};
    setNewEntry({
      id: '',
      name_employee: '',
      current_date: formattedCurrent_Date,
      cost_mileage: '',
      total_mileage_recorded: '',
      total_travel_cost: '',
      travel_date: '',
      time_in: '',
      time_out: '',
      start_address: custom.from || '2300 W Commerce, San Antonio, TX 78207',
      end_address: custom.to || '2300 W Commerce, San Antonio, TX 78207',
      stop_address: [""], //Temp
      contact_person: '',
      odometer_start: '',
      odometer_finish: '',
      mileage: 0,
      status: 'draft',
      notes: '',
      userId: '',
    });
  };

  // MapQuest Functions
  const API_KEY = '4H727f3Nq5ymaqNEeszFgjGOtY0tkiK7'
  const validateAddress = async (address) => {
    try {
      const response = await mapquestFetch(
        `https://www.mapquestapi.com/geocoding/v1/address?key=${API_KEY}&location=${encodeURIComponent(address)}`
      );
      const data = await response.json();

      const location = data.results?.[0]?.locations?.[0];
      const quality = location?.geocodeQualityCode;

      // We check if the address was recognized as a valid address (e.g. P1AAA for exact point)
      if (quality && quality.startsWith('P')) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error al validar dirección:', error);
      return false;
    }
  };

  // validate Address
  const handleValidateAddresses = async () => {
    const isValidFrom = await validateAddress(newEntry.start_address);

    if (!isValidFrom) {
      toast.error(`Invalid start address: ${newEntry.start_address}`);
      return;
    }

    const isValidTo = await validateAddress(newEntry.end_address);
    if (!isValidTo) {
      toast.error(`Invalid end address: ${newEntry.end_address}`);
      return;
    }

    let isValidStop = true;
    const stops = Array.isArray(newEntry.stop_address)
      ? newEntry.stop_address.filter(s => s.trim() !== "")
      : [];

    for (const stop of stops) {
      const valid = await validateAddress(stop);
      if (!valid) {
        isValidStop = false;
        toast.error(`Invalid stop address: ${stop}`);
        return;
      }
    }

    const route = {
      from: newEntry.start_address,
      to: newEntry.end_address,
      stops,
    };
    setSelectedRoute(route);
    setShowMap(true);
    setAddressValidated(true);
  };


  const getMileageBetween = async (start, end) => {

    try {
      const response = await mapquestFetch('https://www.mapquestapi.com/directions/v2/alternateroutes?key=4H727f3Nq5ymaqNEeszFgjGOtY0tkiK7', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          locations: [start, end],
          options: { 
            unit: 'm',
            avoidTimedConditions: true 
          },
        }),
      });
      const data = await response.json();
      console.log(data)
      return data?.route?.distance || 0;
    } catch (error) {
      console.error("Error getting mileage:", error);
      return 0;
    }
  };

  const getDurationFormattedBetween = async (from, to) => {
    const url = `https://www.mapquestapi.com/directions/v2/alternateroutes?key=4H727f3Nq5ymaqNEeszFgjGOtY0tkiK7`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          locations: [from, to],
          options: { unit: 'm' },
        }),
      });

      const data = await response.json();
      const formattedTime = data?.route?.formattedTime; // "HH:MM:SS"

      if (!formattedTime) return '00:00';

      const [hh, mm] = formattedTime.split(':');
      return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}`;
    } catch (error) {
      console.error('Error getting duration:', error);
      return '00:00';
    }
  };


  const handleSaveMapAsImage = () => {
    const mapElement = document.getElementById('map-container');
    if (!mapElement) return;

    html2canvas(mapElement, {
      scale: 3, // Aumenta la resolución (default es 1)
      useCORS: true, // Si tu mapa tiene imágenes externas
    }).then((canvas) => {
      const link = document.createElement('a');
      link.download = `mapa-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    });
  };

  // Ultima dia del mes del viaje
  function getLastDayOfMonth(dateInput) {
    const date = new Date(dateInput);
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0); // Día 0 del siguiente mes = último día del mes actual
  }

  // Save to DB 
  const handleSave = async () => {

    setIsSubmitting(true);
    const missingStopIndex = newEntry.stop_address.findIndex((_, index) => !stopTimes[index]);
    const missingContactPersonIndex = newEntry.stop_address.findIndex((_, index) => !contactPerson[index]);
    const lastIndex = newEntry.stop_address.length;


    if (missingStopIndex !== -1) {
      toast.warning(`⏱️ Please enter the time for Stop #${missingStopIndex + 1}.`);
      setIsSubmitting(false);
      return;
    }


    if (missingContactPersonIndex !== -1) {
      toast.warning(`⏱️ Please enter the Contact Person for Stop #${missingContactPersonIndex + 1}.`);
      setIsSubmitting(false);
      return;
    }

    if (!contactPerson[lastIndex]) {
      toast.warning(`⏱️ Please enter the Contact Person for final destination:`);
      setIsSubmitting(false);
      return;
    }

    if (!newEntry.travel_date || !newEntry.time_in) {
      toast.warning("Travel Date and Driving Start Time are required.");
      setIsSubmitting(false);
      return;
    }

    if (!newEntry.odometer_start) {
      toast.warning("THe Odomete Start is required.");
      setIsSubmitting(false);
      return;
    }

    if (!addressValidated) {
      toast.warning("Please validate the addresses before saving");
      setIsSubmitting(false);
      return;
    }



    const timesArray = [newEntry.time_in, ...Object.values(stopTimes).filter(Boolean)];

    for (let i = 1; i < timesArray.length; i++) {
      const [prevH, prevM] = timesArray[i - 1].split(':').map(Number);
      const [currH, currM] = timesArray[i].split(':').map(Number);
      const prevTotal = prevH * 60 + prevM;
      const currTotal = currH * 60 + currM;

      if (currTotal <= prevTotal) {
        toast.warning(`Start Time for Stop #${i} must be later than the previous one.`);
        setIsSubmitting(false);
        return;
      }
    }

    const currentOdoStart = parseFloat(newEntry.odometer_start);
    const currentOdoFinish = parseFloat(newEntry.odometer_finish);
    const stops = Array.isArray(newEntry.stop_address)
      ? newEntry.stop_address.filter(s => s.trim() !== "")
      : [];

    const userLogs = log
      .filter(entry => entry.userId === userLoggedIn?.id)
      .sort((a, b) => {
        const dateA = new Date(`${a.travel_date}T${a.time_in || '00:00'}`);
        const dateB = new Date(`${b.travel_date}T${b.time_in || '00:00'}`);
        return dateA - dateB;
      });


    const entryDate = new Date(newEntry.travel_date);

    let isReset = false;
    let prevLog = null;

    for (let i = 0; i < userLogs.length; i++) {
      const logDate = new Date(userLogs[i].travel_date);

      if (logDate < entryDate) {
        prevLog = userLogs[i];
      }
    }

    // 🚨 Validation for previous day's odometer
    if (prevLog) {
      const prevOdo = parseFloat(prevLog.odometer_finish);
      if (currentOdoStart < prevOdo) {
        if (!odometerResetConfirmed && currentOdoStart < prevOdo) {
          toast.error(
            `Odometer start (${currentOdoStart}) is lower than the last recorded finish (${prevOdo}).\nIf you switched vehicles, please check the odometer reset box.`
          );
          setIsSubmitting(false);
          return;
        }
        isReset = true;
      }
    }

    // 🚨 Validation for same-day earlier entries
    const sameDayLogs = userLogs.filter(
      entry => new Date(entry.travel_date).toDateString() === entryDate.toDateString()
    );

    const conflictingEntry = sameDayLogs.find(log => {
      const logTime = log.time_in || "00:00";
      const newTime = newEntry.time_in || "00:00";
      return logTime < newTime && parseFloat(log.odometer_finish) > currentOdoStart;
    });

    if (conflictingEntry && !odometerResetConfirmed) {
      toast.error(`This trip starts at ${currentOdoStart}, but an earlier trip on the same day ended at ${conflictingEntry.odometer_finish}. Consider checking the vehicle reset box.`);
      setIsSubmitting(false);
      return;
    }

    // ✅ Enhanced: recursively update ALL future logs where odo finish creates a conflict
    const updateAllRequiredFutureLogs = async (newFinish, fromDate) => {
      const futureLogs = userLogs.filter(entry => new Date(entry.travel_date) > new Date(fromDate));


      let odo = newFinish;
      let updatedIds = [];

      for (let i = 0; i < futureLogs.length; i++) {
        const log = futureLogs[i];
        const originalStart = parseFloat(log.odometer_start);
        const mileage = parseFloat(log.mileage);
        if (odo <= originalStart) continue;

        const newStart = odo + 5;
        const newFinish = odo + mileage;

        await updMileage({
          variables: {
            input: {
              id: log.id,
              odometer_start: newStart,
              odometer_finish: newFinish,
            },
          },
        });

        updatedIds.push(log.id);
        odo = newFinish;
      }

      if (updatedIds.length) {
        setLog(prev =>
          prev.map(e =>
            updatedIds.includes(e.id) ? { ...e, _updated: true } : e
          )
        );

        setTimeout(() => {
          setLog(prev =>
            prev.map(e => {
              const { _updated, ...rest } = e;
              return rest;
            })
          );
        }, 4000);
      }
    };

    if (!isReset) {
      await updateAllRequiredFutureLogs(currentOdoFinish, newEntry.travel_date);
    }

    // 📍 Construir la ruta completa: start → stops... → end
    const rawStops = newEntry.stop_address;
    const stops_leng = Array.isArray(rawStops)
      ? rawStops.filter(s => s.trim() !== "")
      : typeof rawStops === "string" && rawStops.trim() !== ""
        ? [rawStops.trim()]
        : [];

    const hasStop = stops_leng.length > 0;
    const logsToSave = [];


    // Ajustar time_in an Timeout de los stop
    if (hasStop) {

      const stops = Array.isArray(newEntry.stop_address)
        ? newEntry.stop_address.map((stop, i) => ({
          address: typeof stop === 'string' ? stop : stop.address || '',
          contact_person: contactPerson[i] || ''
        }))
        : [];

      const allStops = [newEntry.start_address, ...stops.map(s => s.address), newEntry.end_address];

      const times = [newEntry.time_in, ...stops.map((_, i) => stopTimes[i]), newEntry.time_out];

      let logsToAdd = [];
      let odoStart = parseFloat(newEntry.odometer_start);
      let totalMileage = 0;

      // Obtener total de minutos entre time_in y time_out
      const [hIn, mIn] = newEntry.time_in.split(':').map(Number);
      const [hOut, mOut] = newEntry.time_out.split(':').map(Number);


      for (let i = 0; i < allStops.length - 1; i++) {
        const from = allStops[i];
        const to = allStops[i + 1];
        const mileage_dirty = await getMileageBetween(from, to);
        const mileage = truncateToTwoDecimals(mileage_dirty);

        // console.log(`🚗 Mileage from ${from} to ${to}: ${mileage} miles`);
        const durationFormatted = await getDurationFormattedBetween(from, to); // Ej: '01:25'
        const timeIn = times[i]; // HH:mm
        const [inH, inM] = timeIn.split(':').map(Number);
        const [durH, durM] = durationFormatted.split(':').map(Number);

        const totalMinutes = inH * 60 + inM + durH * 60 + durM;
        const outH = Math.floor(totalMinutes / 60) % 24;
        const outM = totalMinutes % 60;

        const timeOut = `${String(outH).padStart(2, '0')}:${String(outM).padStart(2, '0')}`;
        const sum = odoStart + mileage;
        const sumDecimals = (sum.toString().split('.')[1] || '').length;

        let odoFinish;

        if (sumDecimals > 4) {
          // Round up to 2 decimals
          odoFinish = Math.ceil(sum * 100) / 100;
        } else {
          // Truncate to 2 decimals
          odoFinish = (sum);
        }
        logsToAdd.push({
          ...newEntry,
          start_address: from,
          end_address: to,
          mileage,
          odometer_start: (odoStart),
          odometer_finish: (odoFinish),
          time_in: timeIn,
          time_out: timeOut,
          contact_person: contactPerson[i] || contactPerson[stops.length] || '',
        });

        odoStart = odoFinish;
        totalMileage += mileage;
      }

      // console.log(newEntry);


      logsToSave.push(...logsToAdd);
    }

    // Guardar logs

    else {
      logsToSave.push({
        ...newEntry,
        mileage: (parseFloat(newEntry.mileage)),
        odometer_start: (parseFloat(newEntry.odometer_start)),
        odometer_finish: (parseFloat(newEntry.odometer_finish)),
      });
    }

    try {
      for (const rawEntry of logsToSave) {
        const mileage = parseFloat(rawEntry.mileage);
        const cost = mileage * costPerMile;
        const odStart = parseFloat(rawEntry.odometer_start);
        const odFinish = parseFloat(rawEntry.odometer_finish);
        const formatDate = (date) => date ? new Date(date).toISOString().split('T')[0] : null

        if (isNaN(odStart) || isNaN(odFinish) || odFinish < odStart) {
          toast.error("Please enter valid odometer values.");
          setIsSubmitting(false);
          return;
        }

        if (isNaN(mileage)) {
          toast.error("Mileage must be a valid number.");
          setIsSubmitting(false);
          return;
        }

        if (!userLoggedIn?.id && !rawEntry.userId) {
          toast.error("User ID is missing.");
          setIsSubmitting(false);
          return;
        }

        const entry = {
          name_employee: `${userLoggedIn?.first_name.trim()} ${userLoggedIn?.last_name.trim()}`,
          current_date: formatDate(getLastDayOfMonth(rawEntry.travel_date)),
          travel_date: formatDate(rawEntry.travel_date),
          time_in: rawEntry.time_in,
          time_out: rawEntry.time_out,
          start_address: rawEntry.start_address,
          end_address: rawEntry.end_address,
          contact_person: rawEntry.contact_person,
          odometer_start: odStart,
          odometer_finish: odFinish,
          mileage: mileage,
          cost_mileage: costPerMile,
          total_mileage_recorded: mileage,
          total_travel_cost: cost,
          notes: rawEntry.notes,
          userId: userLoggedIn?.id || rawEntry.userId,
          status: "draft"
        };
        await addMileage({ variables: { input: entry } });
        await loadDraftLogs();
      }
    } catch (err) {
      console.error("❌ Error saving logs:", err);
      toast.error("Error saving logs. Please try again.");
    }

    setIsSubmitting(false);
    setOdoCorrectedOnce(false);
    clearnewEntry();
    setAddressValidated(false);
    setShowForm(false);
  };


  //Update to DB (status:Submitted)
  const handleSubmitAll = async () => {
    const confirmSubmit = window.confirm("Are you sure you want to submit all DraftS logs?");
    if (!confirmSubmit) return;

    try {

      const drafts = log.filter(entry => entry.status === 'draft' && entry.userId === userLoggedIn?.id);

      console.log('Submitting drafts for user:', userLoggedIn?.id, drafts);


      for (const draftEntry of drafts) {

        await updMileage({
          variables: {
            input: {
              id: draftEntry.id,
              status: 'submitted',

            },
          },
        });
      }

      clearnewEntry();
      toast.success('All draft logs submitted successfully.');

      setLog(prev => prev.filter(entry => entry.status !== 'draft'));
      setSubmittedLogs(prev => [...prev, ...drafts.map(entry => ({ ...entry, status: 'submitted' }))]);
    } catch (error) {
      console.error('Error submitting logs:', error);
      toast.error('Error submitting some logs. Try again.');
    }
  };

  // Delete only log from the database
  const handleDelete = async (entry, index) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this entry?");
    if (!confirmDelete) return;

    if (entry.id) {
      try {
        await removeMileage({ variables: { input: { id: entry.id } } });
        // Actualiza tanto borradores como registros enviados si aplica
        setLog((prev) => prev.filter((e) => e.id !== entry.id));
        setSubmittedLogs((prev) => prev.filter((e) => e.id !== entry.id));


        if (typeof fetchMileages === 'function') {
          fetchMileages({ variables: { limit: 1000 } });
        }
        toast.success('The Log was removed successfully.');
      } catch (error) {
        console.error("Error deleting from DB:", error);
        toast.error("Failed to delete entry. Try again.");
      }
    }
  };

  //Delete all selected items from the database
  const handleDeleteSelected = async () => {
    if (!window.confirm("Are you sure you want to delete the selected entries?")) return;

    try {
      for (const id of selectedIds) {
        await removeMileage({ variables: { input: { id } } });
      }


      setLog(prev => prev.filter(e => !selectedIds.includes(e.id)));
      setSubmittedLogs(prev => prev.filter(e => !selectedIds.includes(e.id)));


      setSelectedIds([]);
      fetchMileages();
      toast.success('The Log were removed successfully.');
    } catch (error) {
      console.error("Error deleting selected entries:", error);
      toast.error("Failed to delete selected entries.");
    }
  };

  // List of all logs
  const sortedLog = useMemo(() => {
    const sortableItems = [...log];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const getValue = (item, path) => {
          return path.split('.').reduce((obj, key) => obj?.[key], item);
        };

        const aVal = getValue(a, sortConfig.key);
        const bVal = getValue(b, sortConfig.key);

        // Convertir a formato comparable (número, fecha o string)
        const aCompare = isNaN(aVal) ? (new Date(aVal) !== 'Invalid Date' && !isNaN(new Date(aVal)) ? new Date(aVal) : (aVal || '').toString().toLowerCase()) : parseFloat(aVal);
        const bCompare = isNaN(bVal) ? (new Date(bVal) !== 'Invalid Date' && !isNaN(new Date(bVal)) ? new Date(bVal) : (bVal || '').toString().toLowerCase()) : parseFloat(bVal);

        if (aCompare < bCompare) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aCompare > bCompare) return sortConfig.direction === 'ascending' ? 1 : -1;

        // Si son iguales, ordena por time_in
        const aTime = a.time_in || '';
        const bTime = b.time_in || '';
        return aTime.localeCompare(bTime);;
      });
    }
    return sortableItems;
  }, [log, sortConfig]);


  const deletedUsers = dataUser?.listDeletedUserHistories?.items || [];

  const sortedSubmittedLogs = useMemo(() => {

    const submitted = submittedLogs.filter(entry => entry.status === "submitted");

    const filtered = submitted.filter(entry => {
      // Search entry.user first
      let firstName = entry?.user?.first_name || '';
      let lastName = entry?.user?.last_name || '';

      // Search in the deleted list if it does not exist in user
      if (!firstName && !lastName) {
        const deletedUser = deletedUsers.find(option => option.id === entry.userId);
        if (deletedUser) {
          firstName = deletedUser.first_name || '';
          lastName = deletedUser.last_name || '';
        }
      }
      const fullName = `${firstName} ${lastName}`.toLowerCase();
      const matchesUser = fullName.includes(userSearch.toLowerCase());

      const entryDate = new Date(entry.travel_date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      const inDateRange =
        (!start || entryDate >= start) &&
        (!end || entryDate <= end);

      return matchesUser && inDateRange;
    });

    if (sortConfig !== null) {
      filtered.sort((a, b) => {
        const getValue = (item, path) => {
          return path.split('.').reduce((obj, key) => obj?.[key], item);
        };

        const aVal = getValue(a, sortConfig.key);
        const bVal = getValue(b, sortConfig.key);

        const aCompare = isNaN(aVal) ? (new Date(aVal) !== 'Invalid Date' && !isNaN(new Date(aVal)) ? new Date(aVal) : (aVal || '').toString().toLowerCase()) : parseFloat(aVal);
        const bCompare = isNaN(bVal) ? (new Date(bVal) !== 'Invalid Date' && !isNaN(new Date(bVal)) ? new Date(bVal) : (bVal || '').toString().toLowerCase()) : parseFloat(bVal);

        if (aCompare < bCompare) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aCompare > bCompare) return sortConfig.direction === 'ascending' ? 1 : -1;

        const aTime = a.time_in || '';
        const bTime = b.time_in || '';
        return aTime.localeCompare(bTime);;

      });
    }

    return filtered;
  }, [submittedLogs, sortConfig, userSearch, startDate, endDate]);



  const handleEditLog = (entry) => {
    setNewEntry(entry);
    setEditIndex(log.findIndex(e => e.id === entry.id)); // índice real en el array original
    setOriginalEntry({ ...entry });
    setShowForm(true);
  };

  //Update from DB
  const handleUpdateLog = async () => {

    const updatedEntry = {
      ...newEntry,
      odometer_start: parseFloat(newEntry.odometer_start),
      odometer_finish: parseFloat(newEntry.odometer_finish),
      mileage: parseFloat(newEntry.mileage),
    };

    const addressChanged =
      originalEntry &&
      (originalEntry.start_address !== updatedEntry.start_address ||
        originalEntry.end_address !== updatedEntry.end_address);



    if (addressChanged && !addressValidated) {
      toast.warning("Please validate the addresses before saving.");
      return;
    }

    else {
      const updated = [...log];
      updated[editIndex] = updatedEntry;
      setLog(updated);
      if (updatedEntry.status === "submitted") {
        setSubmittedLogs((prev) =>
          prev.map((log) => (log.id === updatedEntry.id ? updatedEntry : log))
        );
      }

      try {
        await updMileage({
          variables: {
            input: {
              id: updatedEntry.id,
              travel_date: updatedEntry.travel_date,
              time_in: updatedEntry.time_in,
              time_out: updatedEntry.time_out,
              start_address: updatedEntry.start_address,
              end_address: updatedEntry.end_address,
              contact_person: updatedEntry.contact_person,
              odometer_start: updatedEntry.odometer_start,
              odometer_finish: updatedEntry.odometer_finish,
              mileage: updatedEntry.mileage,
              notes: updatedEntry.notes,
              status: updatedEntry.status,
              userId: updatedEntry.userId,
            },
          },
        });

      } catch (error) {
        console.error("Error updating mileage:", error);
      }
    }

    clearnewEntry();
    setEditIndex(null);
    setShowForm(false);
    setAddressValidated(false);
    setOriginalEntry(null);
  };

  function formatDateMMDDYYYY(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const dateObj = new Date(year, month - 1, day); // mes base 0
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    const yyyy = dateObj.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }


  const exportToExcel = (data, userLoggedIn, formattedCurrent_Date, costPerMile, totalMileage, totalCost) => {
    if (!data || data.length === 0) return;

    const titleHeader = [
      ["", "", "", "Mileage Log", "", ""],
      ["", "", "", "The Health Collaborative, Navigator Program", "", ""],
    ];


    const generalHeader = [
      [`Full Name: ${capitalize(userLoggedIn?.first_name)} ${capitalize(userLoggedIn?.last_name)}`, "", "", "", "", ""],
      [`Current Date: ${formatDateMMDDYYYY(formattedCurrent_Date) || ''}`, "", "", "", "", ""],
      [`Estimated Cost per Mile: $${costPerMile?.toFixed(2)}`, "", "", "", "", ""],
      [`Total Mileage Recorded: ${totalMileage?.toFixed(2)} mi`, "", "", "", "", ""],
      [`Total Travel Cost: $${totalCost?.toFixed(2)}`, "", "", "", "", ""],
      [],
    ];


    const columnHeaders = [
      "#",
      "Date",
      "Driving\nStart Time",
      "Driving\nEnd Time",
      "From",
      "To",
      "Contact\nPerson",
      "Odometer\nStart",
      "Odometer\nEnd",
      "Mileage",
      "Event Name or Location"
    ];
    const rows = data.map((item, index) => [
      index + 1,
      formatDateMMDDYYYY(item.travel_date) || '',
      formatToAmPm(item.time_in) || '',
      formatToAmPm(item.time_out) || '',
      item.start_address || '',
      item.end_address || '',
      item.contact_person || '',
      parseFloat(item.odometer_start || 0),
      parseFloat(item.odometer_finish || 0),
      parseFloat(item.mileage || 0),
      item.notes || ''
    ]);
    const fullSheetData = [...titleHeader, ...generalHeader, columnHeaders, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(fullSheetData);

    // Combinar celdas
    worksheet["!merges"] = [
      // Mileage Log: D1:F1 (row 0, col 3–5)
      { s: { r: 0, c: 3 }, e: { r: 0, c: 5 } },

      // The Health Collaborative: D2:F2 (row 1, col 3–5)
      { s: { r: 1, c: 3 }, e: { r: 1, c: 5 } },

      // Resto del encabezado...
      { s: { r: 2, c: 0 }, e: { r: 2, c: 3 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: 5 } },
      { s: { r: 4, c: 0 }, e: { r: 4, c: 5 } },
      { s: { r: 5, c: 0 }, e: { r: 5, c: 5 } },
      { s: { r: 6, c: 0 }, e: { r: 6, c: 5 } },
    ];


    const D1 = worksheet['D1'];
    if (D1) {
      D1.s = {
        font: {
          name: "Arial Narrow",
          sz: 24,
          bold: true,
          color: { rgb: "4F81BD" },
        },
        alignment: {
          horizontal: "center",
          vertical: "center",
          wrapText: true,
        },
      };
    }

    const D2 = worksheet['D2'];
    if (D2) {
      D2.s = {
        font: {
          name: "Arial Narrow",
          sz: 16,
          bold: true,
          color: { rgb: "4F81BD" },
        },
        alignment: {
          horizontal: "center",
          vertical: "center",
          wrapText: true,
        },
      };
    }

    const totalHeaderRows = generalHeader.length + 1;
    const dataRowCount = rows.length;
    worksheet["!rows"] = [];
    worksheet["!rows"][0] = { hpt: 35 }; // Altura para Mileage Log
    worksheet["!rows"][1] = { hpt: 35 }; // Altura para The Health Collaborative

    worksheet["G1"] = { t: "s", v: "  Employee Signature:" };
    worksheet["I1"] = { t: "s", v: "__________________________" };
    worksheet["G2"] = { t: "s", v: "  Supervisor Signature:" };
    worksheet["I2"] = { t: "s", v: "__________________________" };

    const signatureStyle = {
      font: {
        name: "Arial Narrow",
        sz: 16,
        color: { rgb: "000000" }
      },
      alignment: {
        horizontal: "left",
        vertical: "center"
      }
    };

    worksheet["G1"].s = signatureStyle;
    worksheet["I1"].s = signatureStyle;
    worksheet["G2"].s = signatureStyle;
    worksheet["I2"].s = signatureStyle;


    for (let i = 0; i < totalHeaderRows; i++) {
      worksheet["!rows"].push({});
    }
    for (let i = 0; i < dataRowCount; i++) {
      worksheet["!rows"].push({ hpt: 24 });
    }

    // Change the font size of the generalHeader rows
    const generalHeaderStartRow = 2; // r:2 (fila 3 en Excel)
    for (let row = 0; row < generalHeader.length; row++) {
      const excelRow = generalHeaderStartRow + row;
      const colCount = generalHeader[row].length;

      for (let col = 0; col < colCount; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: excelRow, c: col });
        const cell = worksheet[cellAddress];
        if (cell && cell.v !== undefined && cell.v !== "") {
          cell.s = {
            font: {
              name: "Arial Narrow",
              sz: 16,
              color: { rgb: "000000" },
            },
            alignment: {
              horizontal: "left",
              vertical: "center",
            },
          };
        }
      }
    }


    // Apply styles to the header row (the first one after the generalHeader)
    const headerRowIndex = titleHeader.length + generalHeader.length; // = 8
    const columnCount = columnHeaders.length;

    for (let col = 0; col < columnCount; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: headerRowIndex, c: col });
      const cell = worksheet[cellAddress];

      if (cell) {
        cell.s = {
          font: {
            name: "Arial Narrow",
            sz: 14,
            bold: true,
            color: { rgb: "FFFFFF" },

          },
          fill: {
            fgColor: { rgb: "4F81BD" },

          },
          alignment: {
            horizontal: "center",
            vertical: "center",
            wrapText: true
          },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          },
        };
      }
    }

    // 🎯 Change font size of data rows (Data)
    const dataStartRowIndex = 2 + generalHeader.length + 1; // row just after the header
    for (let row = 0; row < rows.length; row++) {
      for (let col = 0; col < columnHeaders.length; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: dataStartRowIndex + row, c: col });
        const cell = worksheet[cellRef];
        if (cell) {
          cell.s = {
            font: {
              name: "Arial Narrow",
              sz: 14,
              color: { rgb: "000000" },
            },
            alignment: {
              vertical: "center",
              horizontal: (col === 0 || col === 1 || col === 2 || col === 3 || col === 4 || col === 5 || col === 6) ? "center" : "left",
              wrapText: true,
            },
            border: {
              top: { style: "thin", color: { rgb: "CCCCCC" } },
              bottom: { style: "thin", color: { rgb: "CCCCCC" } },
              left: { style: "thin", color: { rgb: "CCCCCC" } },
              right: { style: "thin", color: { rgb: "CCCCCC" } },
            },
          };
        }
      }
    }
    if (!worksheet["!rows"]) worksheet["!rows"] = [];

    for (let row = 0; row < rows.length; row++) {
      worksheet["!rows"][dataStartRowIndex + row] = { hpt: 80 };
    }


    //  Adjust column width
    worksheet["!cols"] = [
      { wch: 5 },   // #
      { wch: 15 },  // Date
      { wch: 12 },  // Driving Start Time
      { wch: 12 },  // Driving End Time
      { wch: 20 },  // From
      { wch: 20 },  // To
      { wch: 15 },  // Contact Person
      { wch: 15 },  // Odometer Start
      { wch: 15 },  // Odometer End
      { wch: 12 },  // Mileage
      { wch: 18 },  // Notes
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Mileage Log");

    XLSX.writeFile(workbook, "mileage_log.xlsx", { cellStyles: true });
  };

  const handleMileageUpdate = (miles) => {
    setNewEntry((prev) => ({
      ...prev,
      mileage: miles,
    }));
  };
  const handleTimeOutUpdate = (durationFormatted) => {

    const [durH, durM] = durationFormatted.split(':').map(Number);

    const [inH, inM] = newEntry.time_in.split(':').map(Number);

    const totalMinutes = inH * 60 + inM + durH * 60 + durM;

    // convertir a HH:mm
    const outH = Math.floor(totalMinutes / 60) % 24;
    const outM = totalMinutes % 60;

    const newTimeOut = `${String(outH).padStart(2, '0')}:${String(outM).padStart(2, '0')}`;

    setNewEntry((prev) => ({
      ...prev,
      time_out: newTimeOut,
    }));
  };
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig?.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Add Stop
  const handleStopChange = (index, field, value) => {
    if (field === 'address') {
      const updatedStops = [...newEntry.stop_address];
      updatedStops[index] = value;
      setNewEntry({ ...newEntry, stop_address: updatedStops });
    }

    if (field === 'time_in') {
      setStopTimes(prev => ({
        ...prev,
        [index]: value,
      }));
    }
    if (field === 'contact_person') {
      setContactPerson(prev => ({
        ...prev,
        [index]: value,
      }));
    }
  };


  const addStopField = () => {
    const currentStops = Array.isArray(newEntry.stop_address)
      ? newEntry.stop_address
      : typeof newEntry.stop_address === 'string'
        ? [newEntry.stop_address]
        : [];

    setNewEntry({
      ...newEntry,
      stop_address: [...currentStops, ""],
    });
  };

  // Formato Time
  const formatToAmPm = (time24) => {
    if (!time24) return '';
    const [hour, minute] = time24.split(':');
    const h = parseInt(hour, 10);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minute} ${suffix}`;
  };

  const removeStopField = (indexToRemove) => {

    const updatedStops = newEntry.stop_address.filter((_, i) => i !== indexToRemove);

    const updatedStopTimes = { ...stopTimes };
    delete updatedStopTimes[indexToRemove];


    const reindexedStopTimes = {};
    Object.entries(updatedStopTimes).forEach(([key, value]) => {
      const numericKey = parseInt(key);
      reindexedStopTimes[
        numericKey > indexToRemove ? numericKey - 1 : numericKey
      ] = value;
    });


    const updatedContactPerson = { ...contactPerson };
    delete updatedContactPerson[indexToRemove];

    const reindexedContactPerson = {};
    Object.entries(updatedContactPerson).forEach(([key, value]) => {
      const numericKey = parseInt(key);
      reindexedContactPerson[
        numericKey > indexToRemove ? numericKey - 1 : numericKey
      ] = value;
    });


    setNewEntry({ ...newEntry, stop_address: updatedStops });
    setStopTimes(reindexedStopTimes);
    setContactPerson(reindexedContactPerson);
  };


  return (
    <div className="mileage-container">
      <ToastContainer position="top-center" autoClose={4000} hideProgressBar />
      <header className="mileage-header">
        <h1> Team Mileage Log</h1>
      </header>
      <div className="mileage-summary">

        <div>
          <p>
            <span > Full Name:</span>{' '}
            <span>{capitalize(userLoggedIn?.first_name.trim())} {capitalize(userLoggedIn?.last_name.trim())}</span>
          </p>

          <label >
            Current Date:
            <input
              name="current_date"
              type="date"
              value={newEntry?.current_date}
              onChange={handleChange}

            />
          </label>

          <label >
            Estimated Cost/Mile ($):
            <input
              type="number"
              step="0.01"
              value={costPerMile}
              onChange={(e) => setCostPerMile(parseFloat(e.target.value))}
              disabled={!isAdmin} // ✅ It is enabled only if you are admin
            />
          </label>
        </div>

        <div>
          <p><strong>Total Mileage Recorded:</strong> <span className="text-blue-700">{truncateToTwoDecimals(totalMileage)}</span> mi</p>
          <p><strong>Total Travel Cost:</strong> <span className="text-green-700">${truncateToTwoDecimals(totalCost)}</span></p>
        </div>
      </div>


      <div className='button-entry-logs'> <button
        onClick={() => {
          clearnewEntry();
          setEditIndex(null);
          setShowForm(true);
          setStopTimes({});
          setContactPerson({});

        }}
        className="btn-primary"
      >
        Add New Entry
      </button>
        <button
          onClick={loadSavedLogs}
          className="btn-primary"
        >
          View Submitted Logs
        </button>

      </div>

      {showForm && (
        <div className="mileage-form-overlay" onDoubleClick={(e) => {
          if (e.target.classList.contains("mileage-form-overlay")) {
            clearnewEntry();
            setEditIndex(null);
            setShowForm(false);
            setStopTimes({});
            setContactPerson({});

          }
        }}>
          <div className="mileage-form-container" >
            <h3 > {editIndex !== null ? 'Update' : 'Save'} Mileage Entry</h3>
            <div>

              <label className="flex flex-col">
                Travel Date:
                <input name="travel_date" type="date" value={newEntry?.travel_date} onChange={handleChange} className="border p-2 rounded" />
              </label>
              <label className="flex flex-col">
                Driving Start Time ():
                <input name="time_in" type="time" value={newEntry?.time_in} onChange={handleChange} className="border p-2 rounded" />
              </label>
              <label className="flex flex-col">
                From:
                <input name="start_address" value={newEntry.start_address} readOnly />
              </label>
              {editIndex === null && (
                <label className="flex flex-col">
                  Stops:
                  <span className="text-sm text-gray-600 mb-2">
                    📍 Enter the exact address (e.g. "1526 Semlinger Road, San Antonio, TX 78220")
                  </span>

                  {Array.isArray(newEntry?.stop_address) &&
                    newEntry.stop_address.map((stop, index) => (
                      <div key={index} className="stop-row">
                        <input
                          value={stop.address}
                          onChange={(e) => handleStopChange(index, 'address', e.target.value)}
                          className="stop-input-custom" placeholder={`Stop #${index + 1}`}
                        />

                        <label className="stop-time-label">
                          Contact Person:
                          <input
                            value={contactPerson[index] || ''}
                            onChange={(e) => handleStopChange(index, 'contact_person', e.target.value)}
                            className="stop-time-input-custom" placeholder={` Person #${index + 1}`} />
                        </label>
                        <div className="stop-time-group">
                          <label className="stop-time-label">
                            Start Time {index + 1}:
                            <input
                              type="time"
                              value={stopTimes[index] || ''}
                              onChange={(e) => handleStopChange(index, 'time_in', e.target.value)}
                              className="stop-time-input-custom"
                            />
                          </label>

                        </div>

                        <button
                          type="button"
                          className="stop-remove-btn-custom"
                          onClick={() => removeStopField(index)}

                        >
                          Remove
                        </button>
                      </div>
                    ))}

                  <button
                    type="button"
                    onClick={addStopField}
                    className="add-stop-btn"

                  >
                    + Add Stop
                  </button>
                </label>)}


              <div className="destination-row">
                <label className="flex flex-col">
                  To:
                  <input name="end_address" value={newEntry.end_address} readOnly className="stop-input-custom" />
                </label>

                {editIndex === null && (

                  <label className="stop-time-label">
                    Contact Person for final destination:
                    <input
                      value={contactPerson[newEntry.stop_address?.length] || ''}
                      onChange={(e) =>
                        handleStopChange(newEntry.stop_address?.length, 'contact_person', e.target.value)
                      }
                      className="contactperson-input-custom"
                      placeholder="Supervisor Name"
                    />
                  </label>
                )}
              </div>

              <button
                disabled={!newEntry?.start_address || !newEntry?.end_address}
                onClick={handleValidateAddresses}
              >
                Validate Address
              </button>
              {!addressValidated && (
                <span >
                  ⚠️ Please validate the addresses before saving.
                </span>
              )}

              <label className="flex flex-col">
                This entry is for a different vehicle (odometer reset)
                <input
                  type="checkbox"
                  checked={odometerResetConfirmed}
                  onChange={(e) => setOdometerResetConfirmed(e.target.checked)}
                  className="mr-2"
                />
              </label>
              <label className="flex flex-col">
                Odometer Start:
                <input name="odometer_start" type="number" min="0" value={newEntry?.odometer_start} onChange={handleChange} className="border p-2 rounded" />
              </label>
              <label className="flex flex-col">
                Odometer End:
                <input name="odometer_finish" type="number" value={newEntry?.odometer_finish} readOnly className="border p-2 rounded bg-gray-100" />
              </label>
              <label className="flex flex-col">
                Mileage:
                <input name="mileage" type="number" readOnly value={newEntry?.mileage} onChange={handleChange} className="border p-2 rounded" />
              </label>
              {editIndex !== null && (
                <label className="flex flex-col">
                  Contact Person:
                  <input name="contact_person" type="text" value={newEntry?.contact_person} onChange={handleChange} className="border p-2 rounded" />
                </label>)}
              <label className="flex flex-col">
                Event Name or Location:
                <input name="notes" placeholder="Additional information..." value={newEntry?.notes} onChange={handleChange} className="border p-2 rounded" />
              </label>

              {(isAdmin || isManager) && (
                <label className="flex flex-col">
                  Navigator:
                  <input
                    name="user"
                    readOnly
                    value={(() => {
                      let firstName = newEntry?.user?.first_name || '';
                      let lastName = newEntry?.user?.last_name || '';

                      if (!firstName && !lastName) {
                        const deletedUser = deletedUsers.find(option => option.id === newEntry.userId);
                        if (deletedUser) {
                          firstName = deletedUser.first_name || '';
                          lastName = deletedUser.last_name || '';
                        }
                      }

                      return `${capitalize(firstName)} ${capitalize(lastName)}`.trim();
                    })()}
                    onChange={handleChange}
                    className="border p-2 rounded"
                  />
                </label>
              )}

              {(isAdmin || isManager) && (
                <label className="flex flex-col">
                  Return to Draft Status:
                  <select
                    name="status"
                    value={newEntry?.status}
                    onChange={handleChange}

                  >
                    <option value="submitted">Submitted</option>
                    <option value="draft">Draft</option>
                  </select>
                </label>
              )}

            </div>

            <div className='button-entry-logs'>
              <button onClick={() => setShowForm(false)}>Cancel</button>
              <button onClick={editIndex !== null ? handleUpdateLog : handleSave}
                disabled={isSubmitting || (editIndex === null && !addressValidated && !odoCorrectedOnce)}
                className={`px-4 py-2 border rounded ${editIndex === null && !addressValidated ? 'opacity-50 cursor-not-allowed' : ''}`}>
                {editIndex !== null ? 'Update' : 'Save'}
              </button>

            </div>
          </div>
        </div>

      )}
      <div className="submitted-table-scroll">
        <table className="mileage-table">
          <thead >
            <tr>
              <th >#</th>

              <th onClick={() => requestSort('travel_date')}>Travel Date
                {sortConfig?.key === 'travel_date' && (
                  <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                )}
              </th>
              <th onClick={() => requestSort('time_in')}>Driving Start Time
                {sortConfig?.key === 'time_in' && (
                  <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                )}
              </th>
              <th onClick={() => requestSort('time_out')}>Driving End Time
                {sortConfig?.key === 'time_out' && (
                  <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                )}
              </th>
              <th onClick={() => requestSort('start_address')}>From
                {sortConfig?.key === 'start_address' && (
                  <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                )}
              </th>
              <th onClick={() => requestSort('end_address')}>To
                {sortConfig?.key === 'end_address' && (
                  <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                )}
              </th>
              <th onClick={() => requestSort('contact_person')}>Contact Person
                {sortConfig?.key === 'contact_person' && (
                  <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                )}
              </th>
              <th onClick={() => requestSort('odometer_start')}>Odometer Start
                {sortConfig?.key === 'odometer_start' && (
                  <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                )}
              </th>
              <th onClick={() => requestSort('odometer_finish')}>Odometer Finish
                {sortConfig?.key === 'odometer_finish' && (
                  <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                )}
              </th>
              <th onClick={() => requestSort('mileage')}>Mileage
                {sortConfig?.key === 'mileage' && (
                  <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                )}
              </th>

              <th onClick={() => requestSort('status')}>Status
                {sortConfig?.key === 'status' && (
                  <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                )}
              </th>
              <th onClick={() => requestSort('notes')}>Event Name or Location
                {sortConfig?.key === 'notes' && (
                  <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                )}
              </th>
              {/* <th>View</th>*/}
              <th>Action</th>       </tr>
          </thead>
          <tbody>

            {sortedLog
              .filter(entry => entry.status === "draft" && entry.userId === userLoggedIn?.id)
              .map((entry, index) => (
                <tr
                  key={index}
                  className={`cursor-pointer ${entry._updated ? 'updated-row' : index % 2 === 0 ? 'bg-gray-100' : ''}`}
                  onClick={() => handleEditLog(entry)}
                  ref={entry._updated ? (el) => {
                    if (el) {
                      setTimeout(() => el.classList.add('fade-out'), 100);  // fade
                      setTimeout(() => el.classList.remove('updated-row', 'fade-out'), 4000);  // clean up
                    }
                  } : null}
                >
                  <td >{index + 1}</td>
                  <td className='travel_date'>{formatDateMMDDYYYY(entry.travel_date)}</td>
                  <td className='time_in'>{formatToAmPm(entry.time_in)}</td>
                  <td className='time_out'>{formatToAmPm(entry.time_out)}</td>
                  <td className='start_address'>{entry.start_address}</td>
                  <td className='end_address'>{entry.end_address}</td>
                  <td className='contact_person'>{entry.contact_person}</td>
                  <td className='odometer_start'>{entry?.odometer_start.toLocaleString()}</td>
                  <td className='odometer_finish'>{entry?.odometer_finish.toLocaleString()}</td>
                  <td className='mileage'>{entry.mileage}</td>
                  <td className='status'>{entry.status}</td>
                  <td className='notes' >{entry.notes}</td>

                  {/* <td>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRoute({
                          from: entry.start_address,
                          to: entry.end_address,
                          stops: entry.stop_address ? [entry.stop_address] : [],
                        });
                        setShowMap(true);
                      }}
                      className='view-map-btn'
                    >
                      View Route
                    </button>

                  </td>*/}

                  <td>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(entry, index) }}
                      className="view-map-btn"
                      title="Delete entry"
                    >
                      🗑️
                    </button></td>

                </tr>
              ))}


          </tbody>
        </table></div>
      {sortedLog
        .filter(entry => entry.status === "draft" && entry.userId === userLoggedIn?.id).length > 0 && (
          <div className="submit-logs">
            <p style={{ fontWeight: 'bold', marginBottom: '12px', color: '#23395d', fontSize: '16px' }}>
              ⚠️ Please follow the steps below in order:
            </p>

            <button
              onClick={async () => {
                setIsExporting(true);
                toast.info("Generating PDF... Please wait.");
                await generatePDFWithJsPDF(
                  sortedLog.filter(entry => entry.status === "draft" && entry.userId === userLoggedIn?.id), userLoggedIn
                );
                setIsExporting(false);
              }}
              disabled={isExporting}
              className="btn-small"
            >
              1️⃣ Export Map to PDF
            </button>

            <button
              onClick={async () => {
                setIsExporting(true);
                toast.info("Exporting Excel... Please wait.");

                const filtered = sortedLog.filter(
                  entry => entry.status === "draft" && entry.userId === userLoggedIn?.id
                );

                const firstDraft = filtered[0];
                const currentDate = formatDate(getLastDayOfMonth(firstDraft?.travel_date));

                await exportToExcel(
                  filtered,
                  userLoggedIn,
                  currentDate,
                  costPerMile,
                  totalMileage,
                  totalCost
                );

                setIsExporting(false);
              }}
              disabled={isExporting}
              className="btn-small"
            >
              2️⃣ Export Logs to Excel
            </button>


            <button onClick={handleSubmitAll} className="btn-small">
              3️⃣ Submit All Logs
            </button>
          </div>
        )}

      {showMap && selectedRoute && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            width: '100vw',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowMap(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '1rem',
              borderRadius: '10px',
              width: '90%',
              maxWidth: '800px',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowMap(false)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'red',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                padding: '5px 10px',
                cursor: 'pointer'
              }}
            >
              X
            </button>

            <MapView from={selectedRoute.from || (newEntry.start_address)}
              stops={selectedRoute.stops || (newEntry.stop_address)}
              to={selectedRoute.to || (newEntry.end_address)}
              onMileageCalculated={handleMileageUpdate}
              onTimeOutCalculated={handleTimeOutUpdate}
            />

            <button
              onClick={handleSaveMapAsImage}
              style={{
                marginTop: '10px',
                padding: '8px 16px',
                border: 'none',
                backgroundColor: '#23395d',
                color: 'white',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Save Image
            </button>
          </div>
        </div>
      )}


      {submittedLogs.length > 0 && showSubmittedLogs && (
        <div className="mileage-table-wrapper">
          <h2>Submitted Logs</h2>

          <div className="filter-controls">

            {(isAdmin || isManager) && (
              <input
                type="text"
                placeholder="Search by Nav. Name"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}

              />)}
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}

            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />

            {sortedSubmittedLogs.length > 0 && (
              <button onClick={exportToExcelSubmittedLogs} className="btn-primary">
                Export to Excel
              </button>
            )}


            {selectedIds.length > 0 && (
              <div >
                <button
                  onClick={() => handleBulkStatusChange("draft")}
                  className="change-status-btn"
                >
                  Change Status of Selected ({selectedIds.length})
                </button>
                <button
                  onClick={handleDeleteSelected}
                  className="change-status-btn"
                  style={{ marginLeft: '1rem', backgroundColor: '#e72828', color: 'white' }}
                >
                  Delete Selected ({selectedIds.length})
                </button></div>

            )}
            <button onClick={() => setShowSubmittedLogs(false)} className="btn-primary">
              Close View
            </button>
          </div>

          <div className="submitted-table-scroll">
            <table className="mileage-table">
              <thead>
                <tr>
                  {(isAdmin || isManager) && (
                    <th>
                      <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={allSelected}
                      />
                    </th>)}
                  <th >#</th>
                  <th onClick={() => requestSort('travel_date')}>Travel Date
                    {sortConfig?.key === 'travel_date' && (
                      <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                    )}
                  </th>
                  <th onClick={() => requestSort('time_in')}>Driving Start Time
                    {sortConfig?.key === 'time_in' && (
                      <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                    )}
                  </th>
                  <th onClick={() => requestSort('time_out')}>Driving End Time
                    {sortConfig?.key === 'time_out' && (
                      <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                    )}
                  </th>
                  <th onClick={() => requestSort('start_address')}>From
                    {sortConfig?.key === 'start_address' && (
                      <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                    )}
                  </th>
                  <th onClick={() => requestSort('end_address')}>To
                    {sortConfig?.key === 'end_address' && (
                      <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                    )}
                  </th>
                  <th onClick={() => requestSort('contact_person')}>Contact Person
                    {sortConfig?.key === 'contact_person' && (
                      <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                    )}
                  </th>
                  <th onClick={() => requestSort('odometer_start')}>Odometer Start
                    {sortConfig?.key === 'odometer_start' && (
                      <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                    )}
                  </th>
                  <th onClick={() => requestSort('odometer_finish')}>Odometer Finish
                    {sortConfig?.key === 'odometer_finish' && (
                      <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                    )}
                  </th>
                  <th onClick={() => requestSort('mileage')}>Mileage
                    {sortConfig?.key === 'mileage' && (
                      <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                    )}
                  </th>
                  <th onClick={() => requestSort('user.id')}>Navigator
                    {sortConfig?.key === 'user.id' && (
                      <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                    )}
                  </th>
                  <th onClick={() => requestSort('status')}>Status
                    {sortConfig?.key === 'status' && (
                      <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                    )}
                  </th>
                  <th onClick={() => requestSort('notes')}>Event Name or Location
                    {sortConfig?.key === 'notes' && (
                      <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                    )}
                  </th>
                  {(isAdmin || isManager) && (

                    <th>Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {sortedSubmittedLogs.map((entry, index) => (
                  <tr key={entry.id} className={index % 2 === 0 ? 'bg-gray-100 cursor-pointer' : 'cursor-pointer'}
                    onClick={(isAdmin || isManager) ? () => handleEditLog(entry) : undefined}>
                    {(isAdmin || isManager) && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"

                          onChange={() => handleCheckboxChange(entry.id)}
                          checked={selectedIds.includes(entry.id)}

                        />
                      </td>)}

                    <td>{index + 1}</td>
                    <td className="travel_date">{formatDateMMDDYYYY(entry.travel_date)}</td>
                    <td className='time_in'>{formatToAmPm(entry.time_in)}</td>
                    <td className='time_out'>{formatToAmPm(entry.time_out)}</td>
                    <td className="start_address">{entry.start_address}</td>
                    <td className="end_address">{entry.end_address}</td>
                    <td className="contact_person">{entry.contact_person}</td>
                    <td className="odometer_start">{entry?.odometer_start.toLocaleString()}</td>
                    <td className="odometer_finish">{entry?.odometer_finish.toLocaleString()}</td>
                    <td className="mileage">{entry.mileage}</td>
                    <td className="user.id">
                      {(() => {
                        // Primero tratamos de usar el usuario actual
                        let firstName = entry?.user?.first_name || '';
                        let lastName = entry?.user?.last_name || '';

                        // Si no existe, buscar en usuarios eliminados
                        if (!firstName && !lastName) {
                          const deletedUser = deletedUsers.find(option => option.id === entry.userId);
                          if (deletedUser) {
                            firstName = deletedUser.first_name || '';
                            lastName = deletedUser.last_name || '';
                          }
                        }

                        return `${capitalize(firstName)} ${capitalize(lastName)}`.trim();
                      })()}
                    </td>

                    <td className="status">{entry.status}</td>
                    <td className="notes">{entry.notes}</td>

                    {(isAdmin || isManager) && (
                      <td>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(entry, index) }}
                          title="Delete entry"
                          className="view-map-btn"
                        >
                          🗑️
                        </button></td>
                    )}

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}
